/**
 * Match Pairs Exercise (INT-CMP-005)
 * User matches items from left column with right column.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface Pair {
  left: string;
  right: string;
}

interface MatchPairsExerciseProps {
  prompt: string;
  pairs: Pair[];
  hint?: string;
  onComplete: (correct: boolean, matchedCount: number) => void;
}

export const MatchPairsExercise = React.memo(function MatchPairsExercise({
  prompt,
  pairs,
  hint,
  onComplete,
}: MatchPairsExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  // Shuffle right column — intentional: shuffle pairs once when they change
  /* eslint-disable react-hooks/purity */
  const shuffledRight = useMemo(() => {
    const items = pairs.map((p) => p.right);
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [pairs]);
  /* eslint-enable react-hooks/purity */

  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [matches, setMatches] = useState<Record<number, number>>({}); // left index → right index
  const [correctMatches, setCorrectMatches] = useState<Set<number>>(new Set());
  const [wrongMatch, setWrongMatch] = useState<{ left: number; right: number } | null>(null);
  const [completed, setCompleted] = useState(false);

  const handleLeftPress = useCallback((index: number) => {
    if (completed || correctMatches.has(index)) return;
    setSelectedLeft(index);
    setWrongMatch(null);
  }, [completed, correctMatches]);

  const handleRightPress = useCallback((rightIndex: number) => {
    if (completed || selectedLeft === null) return;

    // Check if this right item is already matched
    const alreadyMatched = Object.values(matches).includes(rightIndex);
    if (alreadyMatched) return;

    const leftItem = pairs[selectedLeft];
    const rightItem = shuffledRight[rightIndex];

    if (leftItem.right === rightItem) {
      // Correct match
      const newMatches = { ...matches, [selectedLeft]: rightIndex };
      const newCorrect = new Set(correctMatches);
      newCorrect.add(selectedLeft);
      setMatches(newMatches);
      setCorrectMatches(newCorrect);
      setSelectedLeft(null);
      setWrongMatch(null);

      // Check if all matched
      if (newCorrect.size === pairs.length) {
        setCompleted(true);
        onComplete(true, newCorrect.size);
      }
    } else {
      // Wrong match — flash briefly
      setWrongMatch({ left: selectedLeft, right: rightIndex });
      setTimeout(() => {
        setWrongMatch(null);
        setSelectedLeft(null);
      }, 800);
    }
  }, [completed, selectedLeft, matches, correctMatches, pairs, shuffledRight, onComplete]);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: t.text, ...ffs }}
        >
          {prompt}
        </Text>

        {hint && !completed && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Two columns */}
        <HStack space="md">
          {/* Left column */}
          <VStack flex={1} space="xs">
            {pairs.map((pair, idx) => {
              const isMatched = correctMatches.has(idx);
              const isSelected = selectedLeft === idx;
              const isWrong = wrongMatch?.left === idx;

              return (
                <TouchableOpacity
                  key={`left-${idx}`}
                  testID={`match-left-${idx}`}
                  accessibilityLabel={pair.left}
                  accessibilityRole="button"
                  onPress={() => handleLeftPress(idx)}
                  disabled={isMatched || completed}
                  activeOpacity={0.7}
                >
                  <Box
                    p="$2"
                    rounded="$md"
                    alignItems="center"
                    style={{
                      backgroundColor: isMatched
                        ? '#D1FAE5'
                        : isWrong
                          ? '#FEE2E2'
                          : isSelected
                            ? colors.primary
                            : (t.isDark ? '#374151' : '#F3F4F6'),
                      borderWidth: 1,
                      borderColor: isMatched
                        ? '#059669'
                        : isWrong
                          ? '#DC2626'
                          : isSelected
                            ? colors.primary
                            : t.border,
                    }}
                  >
                    <Text
                      allowFontScaling={false}
                      numberOfLines={2}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '500',
                        textAlign: 'center',
                        color: isMatched
                          ? '#065F46'
                          : isWrong
                            ? '#991B1B'
                            : isSelected
                              ? '#FFFFFF'
                              : t.text,
                        ...ffs,
                      }}
                    >
                      {pair.left}
                    </Text>
                  </Box>
                </TouchableOpacity>
              );
            })}
          </VStack>

          {/* Right column */}
          <VStack flex={1} space="xs">
            {shuffledRight.map((item, idx) => {
              const isMatched = Object.values(matches).includes(idx);
              const isWrong = wrongMatch?.right === idx;

              return (
                <TouchableOpacity
                  key={`right-${idx}`}
                  testID={`match-right-${idx}`}
                  accessibilityLabel={item}
                  accessibilityRole="button"
                  onPress={() => handleRightPress(idx)}
                  disabled={isMatched || completed || selectedLeft === null}
                  activeOpacity={0.7}
                >
                  <Box
                    p="$2"
                    rounded="$md"
                    alignItems="center"
                    style={{
                      backgroundColor: isMatched
                        ? '#D1FAE5'
                        : isWrong
                          ? '#FEE2E2'
                          : (t.isDark ? '#374151' : '#F3F4F6'),
                      borderWidth: 1,
                      borderColor: isMatched
                        ? '#059669'
                        : isWrong
                          ? '#DC2626'
                          : t.border,
                      opacity: isMatched ? 0.6 : 1,
                    }}
                  >
                    <Text
                      allowFontScaling={false}
                      numberOfLines={2}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '500',
                        textAlign: 'center',
                        color: isMatched ? '#065F46' : isWrong ? '#991B1B' : t.text,
                        ...ffs,
                      }}
                    >
                      {item}
                    </Text>
                  </Box>
                </TouchableOpacity>
              );
            })}
          </VStack>
        </HStack>

        {/* Completion message */}
        {completed && (
          <Box p="$2" rounded="$md" style={{ backgroundColor: '#D1FAE5' }}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#065F46', textAlign: 'center', ...ffs }}
            >
              ✓ All pairs matched!
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
